import React from "react";
import { useHistory } from "react-router-dom";

import { useOngoingCall } from "service/realtime/CallEventsContext";
import { useActiveCallMaybe } from "modules/patient/components/ActiveCallProvider";
import { useMobileLayer } from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobileCallBar from "mobile/components/MobileCallBar";
import "../styles/sparktgCallFloater.scss";

/**
 * Persistent pill shown while a SparkTG prescheduled call is in progress.
 *
 * Mounted once at the App root above <Router> so it survives every
 * navigation. Click routes to the appointment's patient page
 * (/navigo-module/patient/:appointmentId).
 *
 * Mutual exclusion with the LiveKit CallFloater: a doctor should see
 * exactly one call indicator at a time. If an in-app LiveKit call is
 * live (ActiveCallProvider.callActive), suppress this one — the
 * LiveKit floater is the richer control (restore/hangup). SparkTG is
 * a phone-only channel; nothing to interact with here.
 *
 * Visuals match the ZY-10312 mock: peach circle with an orange phone,
 * green pulse dot, bold patient name, grey "Appointment ID — N".
 */

/* Filled phone glyph — path lifted verbatim from the ZY-10312 mock
   (filter0_d_8840_8048), viewBox cropped to its native bounding box so
   it draws at 13.5×13.5 inside the 36px tile without scaling drift. */
const PhoneIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="55.25 39.25 13.5 13.5"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M68.0117 48.5312C68.2227 48.5312 68.3984 48.6016 68.5391 48.7422C68.6797 48.8828 68.75 49.0586 68.75 49.2695V51.8711C68.75 52.457 68.5039 52.75 68.0117 52.75C64.5195 52.75 61.5195 51.4961 59.0117 48.9883C56.5039 46.4805 55.25 43.4805 55.25 39.9883C55.25 39.4961 55.543 39.25 56.1289 39.25H58.7305C58.9414 39.25 59.1172 39.3203 59.2578 39.4609C59.3984 39.6016 59.4688 39.7773 59.4688 39.9883C59.4688 40.9023 59.6094 41.7812 59.8906 42.625C59.9844 42.9297 59.9258 43.1875 59.7148 43.3984L58.2734 44.6641C59.3516 46.9141 61.0742 48.6133 63.4414 49.7617L64.6016 48.2852C64.7422 48.1445 64.918 48.0742 65.1289 48.0742C65.2461 48.0742 65.3281 48.0859 65.375 48.1094C66.2188 48.3906 67.0977 48.5312 68.0117 48.5312Z" />
  </svg>
);

function SparkTGCallFloater() {
  const history = useHistory();
  const ongoingCall = useOngoingCall();
  // Returns null when the floater is mounted above <ActiveCallProvider>;
  // use the "Maybe" variant so missing context does not throw.
  const activeCall = useActiveCallMaybe();
  const livekitCallUp = !!(activeCall && activeCall.callActive);
  // Phone screens: a row above row 1 instead of the pill (mobileConfig callBar).
  const mobileBar = useMobileLayer() && MOBILE.callBar;

  if (!ongoingCall || !ongoingCall.appointmentId) return null;
  // Only one call indicator at a time — defer to the richer LiveKit UI.
  if (livekitCallUp) return null;

  const goToAppointment = () => {
    history.push(`/navigo-module/patient/${ongoingCall.appointmentId}`);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToAppointment();
    }
  };

  const patientName = ongoingCall.patientName || "Ongoing call";

  if (mobileBar) {
    return (
      <MobileCallBar
        patientName={patientName}
        appointmentId={ongoingCall.appointmentId}
        icon={<PhoneIcon />}
        onOpen={goToAppointment}
      />
    );
  }

  return (
    <div
      className="sparktg-floater"
      role="button"
      tabIndex={0}
      onClick={goToAppointment}
      onKeyDown={onKeyDown}
      aria-label={`Ongoing call with ${patientName}, open appointment ${ongoingCall.appointmentId}`}
      title="Open appointment"
    >
      <span className="sparktg-floater-icon" aria-hidden="true">
        <PhoneIcon />
      </span>
      <span className="sparktg-floater-body">
        <span className="sparktg-floater-name">
          <span className="sparktg-floater-dot" aria-hidden="true" />
          {patientName}
        </span>
        <span className="sparktg-floater-sub">
          Appointment ID - {ongoingCall.appointmentId}
        </span>
      </span>
    </div>
  );
}

export default SparkTGCallFloater;
